// @flow
import * as React from 'react';
import numeral from 'numeral';
import classnames from 'classnames';
import { CopyOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Popconfirm } from 'antd';
import { SortableElement } from 'react-sortable-hoc';
import { calculateLinearMeterCO2, calculateStandardCO2 } from '../../../../../Utils/co2Calculator';
import styles from './GroupFooter.css';

import type {
  EstimateGroup,
  AddEstimateItemParams,
  RemoveEstimateGroupParams,
} from '../../../types';

type Props = {
  editable: boolean,
  estimateGroup: EstimateGroup,
  estimateGroupIndex: number,
  onClickAddEstimateItem: (params: AddEstimateItemParams) => void,
  onClickDuplicateEstimateGroup: (params: any) => void,
  onClickRemoveEstimateGroup: (params: RemoveEstimateGroupParams) => void,
}

class Component extends React.PureComponent<Props> {

  render () {

    const {
      editable,
      estimateGroup,
      estimateGroupIndex,
      onClickAddEstimateItem,
      onClickDuplicateEstimateGroup,
      onClickRemoveEstimateGroup
    } = this.props;

    const groupTotal = estimateGroup.estimate_items
      .filter(item => item.flag !== 'Omit' && item.flag !== 'By Others')
      .reduce((total, estimateItem) => total + Number(estimateItem.total), 0)

    // Calculate CO2 used for the group
    console.log('Group items:', estimateGroup.estimate_items);

    const groupCO2 = estimateGroup.estimate_items
      .filter(item => item.flag !== 'Omit' && item.flag !== 'By Others')
      .reduce((totalCO2, estimateItem, index) => {
        console.log(`Processing item ${index}:`, estimateItem);
        console.log(`Item type: ${estimateItem.type}, unit: ${estimateItem.unit}, quantity: ${estimateItem.quantity}`);
        console.log(`Has paint_data: ${!!estimateItem.paint_data}`);

        // Calculate CO2 for paint items (type D) with either M2 unit or M unit with coverage data
        if (estimateItem.type === 'D' && estimateItem.quantity &&
            ((estimateItem.unit === 'm2' && estimateItem.paint_data) ||
             (estimateItem.unit === 'm' && estimateItem.coverage_data))) {
          try {
            let coverage = 0;
            let carbon = 0;
            let coats = 1;
            const quantity = parseFloat(estimateItem.quantity || 0);

            // Handle different data sources based on unit type
            if (estimateItem.unit === 'm2' && estimateItem.paint_data) {
              // Parse the paint data JSON for m2 unit
              const paintData = JSON.parse(estimateItem.paint_data);
              console.log('Paint data for m2:', paintData);

              // Get coverage, carbon, and coats values from paint data
              // Handle both camelCase and lowercase property names for compatibility
              coverage = parseFloat(paintData['coverage'] || paintData['Coverage'] || 0);
              carbon = parseFloat(paintData['carbon'] || paintData['Carbon'] || 0);
              coats = parseFloat(paintData['coats'] || paintData['Coats'] || 1); // Default to 1 coat if not specified
              //coverage = 1;

              // If we still don't have values, try alternative property names
              if (coverage === 0) coverage = parseFloat(paintData['Coverage (m2 PL)'] || 0);
              if (carbon === 0) carbon = parseFloat(paintData['Carbon (m2 PL)'] || 0);

              console.log(`m2 unit - Coverage: ${coverage}, Carbon: ${carbon}, Coats: ${coats}, Quantity: ${quantity}`);
            }
            else if (estimateItem.unit === 'm' && estimateItem.coverage_data) {
              // Parse the coverage data JSON for m unit
              const coverageData = JSON.parse(estimateItem.coverage_data);
              console.log('Coverage data for linear meters:', coverageData);

              // Get coverage, carbon, coats, and paint coverage values from coverage data
              coverage = parseFloat(coverageData.coverage || 0);
              carbon = parseFloat(coverageData.carbon || 0);
              coats = parseFloat(coverageData.coats || 1); // Default to 1 coat if not specified
              const paintCoverage = parseFloat(coverageData.paint_coverage || 1); // Default to 1 if not specified

              console.log(`Linear meters - Coverage: ${coverage}, Paint Coverage: ${paintCoverage}, Carbon: ${carbon}, Coats: ${coats}, Quantity: ${quantity}`);

              // Calculate CO2 for linear meters using centralized function
              const co2Used = calculateLinearMeterCO2(quantity, coverage, paintCoverage, carbon, coats);
              return totalCO2 + co2Used;
            }

            // Calculate CO2 used for standard calculations
            const co2Used = calculateStandardCO2(quantity, coverage, carbon, coats);

            console.log(`Running total CO2: ${totalCO2} + ${co2Used} = ${totalCO2 + co2Used}`);
            return totalCO2 + co2Used;
          } catch (e) {
            console.error('Error calculating CO2:', e);
            return totalCO2;
          }
        }
        return totalCO2;
      }, 0);

    console.log('Final group CO2:', groupCO2);

    return (
      <div
        className={styles.wrapper}
      >

        {editable && (

          <React.Fragment>

            <div
              className={classnames(styles.link, styles.addLink)}
              onClick={() => onClickAddEstimateItem({ estimateGroupIndex })}
              role="presentation"
            >
              <PlusOutlined />
              {' Add Item'}
            </div>

            <div
              className={classnames(styles.link)}
              onClick={() => onClickDuplicateEstimateGroup({ estimateGroupIndex })}
              role="presentation"
            >
              <CopyOutlined />
              {' Duplicate Group'}
            </div>

            <Popconfirm
              title="Remove this group?"
              onConfirm={() => onClickRemoveEstimateGroup({ estimateGroupIndex })}
              okText="Yes"
              cancelText="No"
            >
              <div
                className={classnames(styles.link, styles.removeLink)}
                role="presentation"
              >
                <DeleteOutlined />
                {' Remove Group'}
              </div>
            </Popconfirm>

          </React.Fragment>

        )}


        <div className={styles.total}>
          {`Total for Group: £${numeral(groupTotal).format('0,0.00')} | CO2 Used: ${numeral(groupCO2).format('0,0.00')} kg`}
        </div>

      </div>
    );
  }
}

export default SortableElement(Component);
